import { type FeatureFlagKey, type FeatureFlagMap, PROJECT_LEVEL_ROUTE_PATHS } from "@repo/shared";
import { BookOpen, FileText, History, House, MessageSquareText, Shield } from "lucide-react";
import type { ElementType } from "react";
import { ClaudiaIcon } from "@/components/claudia-icon";

/**
 * The project root path, as `normalizeProjectPath` produces it. There is no `welcome` entry in
 * `PROJECT_LEVEL_ROUTE_PATHS` because `/` is the project itself rather than a screen hung off
 * it; `parseProjectPath` special-cases it ahead of that table.
 */
export const PROJECT_ROOT_PATH = "/";

/**
 * The Claudia nav row's node id. It is the one project-level row that does not navigate: the
 * sidebar keys off this id to hang the conversations flyout and the needs-you dot on it, and
 * the collapsed rail keys off it to pick its own Claudia renderer. The id is a contract
 * between the list and both sidebars, not a label.
 */
export const PROJECT_LEVEL_CLAUDIA_NODE_ID = "project-level:claudia";

export interface ProjectLevelNavItem {
  id: string;
  label: string;
  icon: ElementType;
  path: string;
  featureFlag?: FeatureFlagKey;
}

/**
 * The pinned block at the top of the project sidebar — everything that belongs to the project
 * rather than to a framework root folder. The expanded sidebar and the collapsed rail both
 * derive order and membership from this one list, so a reorder cannot land in one and miss the
 * other. `splitProjectSidebarRoots` keys off the `project-level:` id prefix, so the prefix is
 * load-bearing, not decoration.
 *
 * Project home is first and its path is the project root `/`. Selection is exact-match on that
 * path (`locateTreeNodeByLocation` looks the location key up directly, and the prefix walk it
 * falls back to stops before the leading slash), so no deeper page — `/documents` included —
 * ever lights this row up.
 */
export const PROJECT_LEVEL_NAV_ITEMS: readonly ProjectLevelNavItem[] = [
  {
    id: "project-level:home",
    label: "Project home",
    icon: House,
    path: PROJECT_ROOT_PATH,
  },
  {
    id: PROJECT_LEVEL_CLAUDIA_NODE_ID,
    label: "Claudia",
    icon: ClaudiaIcon,
    path: PROJECT_LEVEL_ROUTE_PATHS.aiAssistant,
  },
  {
    id: "project-level:documents",
    label: "Documents",
    icon: FileText,
    path: PROJECT_LEVEL_ROUTE_PATHS.projectDocuments,
  },
  {
    id: "project-level:knowledge",
    label: "Knowledge",
    icon: BookOpen,
    path: PROJECT_LEVEL_ROUTE_PATHS.knowledge,
  },
  {
    id: "project-level:quality-guard",
    label: "Quality Guard",
    icon: Shield,
    path: PROJECT_LEVEL_ROUTE_PATHS.specificationQualityGuard,
  },
  {
    id: "project-level:clarification-requests",
    label: "Clarification Requests",
    icon: MessageSquareText,
    path: PROJECT_LEVEL_ROUTE_PATHS.specificationClarificationRequests,
  },
  {
    id: "project-level:version-snapshots",
    label: "Version Snapshots",
    icon: History,
    path: PROJECT_LEVEL_ROUTE_PATHS.specificationVersionSnapshots,
    featureFlag: "version_snapshots",
  },
];

/**
 * The items a caller holding `flags` may see, in list order.
 *
 * A gated item is drawn only on a resolved `true`; a pending map reads every flag as `false`
 * and therefore hides it. Hiding an item that turns out to be enabled only delays it by one
 * round trip, while showing one that turns out to be disabled flashes a destination whose page
 * is a not-found.
 */
export function getVisibleProjectLevelNavItems(
  flags: FeatureFlagMap
): readonly ProjectLevelNavItem[] {
  return PROJECT_LEVEL_NAV_ITEMS.filter(
    (item) => item.featureFlag === undefined || flags[item.featureFlag] === true
  );
}

/**
 * The items the collapsed rail draws, in rail order. It is the whole visible project-level
 * list: the rail shows the same destinations as the expanded sidebar, and differs only in how
 * it renders the Claudia row.
 */
export function getCollapsedRailItems(flags: FeatureFlagMap): readonly ProjectLevelNavItem[] {
  return getVisibleProjectLevelNavItems(flags);
}

/**
 * Whether `currentPath` is inside a rail item's destination. Every screen matches on its path
 * prefix so that a sub-route (`/ai-assistant/thread/…`, `/quality-guard/configuration`) keeps
 * its row lit; Project home is exact-match, because every path starts with `/`.
 */
export function isProjectLevelNavItemActive(
  item: ProjectLevelNavItem,
  currentPath: string
): boolean {
  if (item.path === PROJECT_ROOT_PATH) {
    return currentPath === PROJECT_ROOT_PATH;
  }

  return currentPath.startsWith(item.path);
}
